import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { BuyerCallAnswers, BuyerPrequalCallStateDto, BuyerPrequalCallSubmissionDto } from "@/api/prequalCall";
import type { CallAnswersPatch } from "./usePrequalCall";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import {
  isCompanyBuyerType,
  type PrequalBuyerType,
  type PrequalEquitySource,
  type PrequalInternalRelationship,
  type PrequalOperatorArrangement,
} from "@/portal/api/prequalification";
import {
  COMPANY_FIELD_LIMITS,
  OTHER_ASSET_CATEGORIES,
  type OtherAssetKey,
} from "@/portal/pages/prequalify/BuyerPrequalifyPage";
import {
  blockSlug,
  ChoiceField,
  CurrencyField,
  KnockoutChecklist,
  optionsFromCopy,
  PhoneField,
  formatMoneyTyping,
  parseMoney,
  questionBlock,
  type QuestionCopy,
} from "@/portal/pages/prequalify/PrequalBits";
import {
  BUYER_KNOCKOUTS,
  DOCUMENT_PATH_CARDS,
  DOCUMENT_PATH_INTERNAL,
  DOCUMENT_PATH_WITH_DEAL_SHEET,
  INDUSTRY_LADDER,
  INTERNAL_RELATIONSHIP_OPTIONS,
  INTERNAL_RELATIONSHIP_WITH_TENURE,
  INVOLVEMENT_OPTIONS,
  MANAGEMENT_LADDER,
  OWNERSHIP_LADDER,
  PRICE_BANDS,
  RESERVES_BANDS,
  YES_NO_OPTIONS,
} from "@/portal/pages/prequalify/prequalContent";
import {
  ListingPicker,
  resolvePickedListingCashFlow,
  type PickedListing,
} from "../presentBuyer/PresentBuyerPickers";
import { callCopy, type CallFunnelCopy, type CallFunnelCopyBlock } from "./callFunnelCopy";

/**
 * The call's prequalification half, as fields inside the interview's own
 * sections — the public funnel's questions in the funnel's own words.
 *
 * <p>🔴 <b>The wording is the funnel's, read by the same block keys.</b> The
 * buyer is asked on the phone exactly what they would have been asked on the
 * funnel, so the answers mean the same thing to the engine. Every question,
 * option label and helper is a Document Language `funnel-copy` block (M1, D7):
 * it renders its PUBLISHED text from the call state through `callCopy`, the
 * funnel's compiled default only when the state carries none, so an edit there
 * reaches the funnel and the call alike (`callFunnelCopy.test.ts` pins that both
 * read the same key for each shared question). Option VALUES stay coded. The
 * call's own blocks are the two one-tap follow-ups the interview derivation
 * needs, the "won't share" toggle and the path heading; the listing caution
 * (Q12) is the one coded sentence.</p>
 *
 * <p>Every answer is optional and null means "not asked yet" — unlike the
 * funnel, whose defaults cannot tell a skipped question from an answer.</p>
 */

export interface CallFieldsProps {
  answers: BuyerCallAnswers;
  onChange: (patch: CallAnswersPatch) => void;
  /** The published `funnel-copy` wording from the call state, by block key. */
  funnelCopy?: CallFunnelCopy;
}

/** The caution beside a cash-flow figure prefilled from a listing (ruling Q12). */
export const LISTING_CASH_FLOW_CAUTION =
  "Listing figure, basis unknown — confirm it is free cash flow.";

/** The call's reader for a funnel question block (D7). */
function callQuestions(funnelCopy: CallFunnelCopy): QuestionCopy {
  return (block) => callCopy(funnelCopy, block as CallFunnelCopyBlock);
}

/** A stored figure as the currency field shows it: grouped digits, blank for
 *  "not asked". Zero shows as 0 — on the fields where zero is an answer. */
function moneyText(value: number | null | undefined): string {
  return value == null ? "" : formatMoneyTyping(String(Math.round(value)));
}

function yesNo(value: boolean | null | undefined): string | null {
  return value == null ? null : value ? "yes" : "no";
}

function wholeNumber(raw: string): number | null {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const isCompany = (answers: BuyerCallAnswers) => isCompanyBuyerType(answers.buyerType ?? null);

/**
 * Which full-time-management follow-up MF asks — the funnel's structure (FT):
 * none for the operator; a company's own question then AAMA; an individual
 * overseer's five-choice question; a hands-off individual's AAMA question only
 * (D2). `unasked` until involvement is answered.
 */
type CallFollowUp = "unasked" | "none" | "company" | "overseer" | "hands-off";

function callFollowUp(answers: BuyerCallAnswers): CallFollowUp {
  if (answers.involvement == null) return "unasked";
  if (answers.involvement === "operator") return "none";
  if (isCompany(answers)) return "company";
  return answers.involvement === "oversight-manager" ? "overseer" : "hands-off";
}

/**
 * The patch that clears the follow-up answers a change of buyer type or
 * involvement makes stale, as the funnel does: becoming the operator clears
 * them all; any other change of follow-up clears the arrangement and its name
 * (no DesignatedPerson for a company, no operator answer for a hands-off
 * individual) and keeps only an AAMA "yes", which means the same thing in every
 * follow-up. Only fields that change are in the patch.
 */
function staleFollowUpClears(prev: BuyerCallAnswers, next: BuyerCallAnswers): Partial<BuyerCallAnswers> {
  const after = callFollowUp(next);
  if (after === "none") return { aamaPath: null, operatorArrangement: null, operatorName: null };
  if (callFollowUp(prev) === after) return {};
  const clears: Partial<BuyerCallAnswers> = {};
  if (prev.operatorArrangement != null) clears.operatorArrangement = null;
  if (prev.operatorName != null) clears.operatorName = null;
  if (prev.aamaPath === false) clears.aamaPath = null;
  return clears;
}

/** The individual overseer's five choices: three arrangements (their wire
 *  names) and the two that designate nobody. */
type ManagerChoice = PrequalOperatorArrangement | "aama" | "undecided";

const INDIVIDUAL_ARRANGEMENTS: readonly PrequalOperatorArrangement[] = [
  "HiredGeneralManager",
  "ExistingManagement",
  "DesignatedPerson",
];

/** The five-choice answer the call holds: an individual arrangement first (the
 *  designated operator wins over AAMA, ruling Q1), then AAMA yes, then AAMA no as
 *  "Not decided yet"; null is not asked yet. */
function managerChoiceOf(answers: BuyerCallAnswers): ManagerChoice | null {
  const arrangement = INDIVIDUAL_ARRANGEMENTS.find((a) => a === answers.operatorArrangement);
  if (arrangement) return arrangement;
  if (answers.aamaPath === true) return "aama";
  if (answers.aamaPath === false) return "undecided";
  return null;
}

/** A five-choice pick as call answers — the funnel's mapping: a designated
 *  choice is not AAMA (false); AAMA is true with no arrangement; "Not decided
 *  yet" is false with none. A designated choice keeps a name already typed. */
function managerChoicePatch(choice: string): Partial<BuyerCallAnswers> {
  if (choice === "aama") return { aamaPath: true, operatorArrangement: null, operatorName: null };
  if (choice === "undecided") return { aamaPath: false, operatorArrangement: null, operatorName: null };
  return { aamaPath: false, operatorArrangement: choice as PrequalOperatorArrangement };
}

/** Fields a section renders above its interview questions, headed so the
 *  specialist can see which answers feed the prequalification. */
function CallBlock({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <div
      className="grid gap-space-3 rounded-md border border-border-light p-space-3"
      data-testid={testId}
    >
      {children}
    </div>
  );
}

// ── Financial capacity ───────────────────────────────────────────────────

/**
 * FC on the call: who is buying (and the acquiring company), the exact cash for
 * the down payment or "won't share" (M5), the non-cash assets an individual
 * names, working capital and its band, and — when no deal is being priced — the
 * price band.
 */
export function CallFinancialFields({ answers, onChange, funnelCopy }: CallFieldsProps) {
  const copy = (block: CallFunnelCopyBlock) => callCopy(funnelCopy, block);
  const company = isCompany(answers);
  const pricesADeal =
    answers.documentPath === DOCUMENT_PATH_WITH_DEAL_SHEET
    || answers.documentPath === DOCUMENT_PATH_INTERNAL;

  return (
    <CallBlock testId="call-fields-FC">
      <ChoiceField
        label={copy("question-buyer-type")}
        options={[
          { value: "Individual", label: copy("option-buyer-type-individual") },
          { value: "CompanyOwnedOrLed", label: copy("option-buyer-type-company") },
          { value: "InvestmentGroupOrFund", label: copy("option-buyer-type-fund") },
        ]}
        value={answers.buyerType ?? null}
        onChange={(v) => {
          const buyerType = v as PrequalBuyerType;
          onChange({ buyerType, ...staleFollowUpClears(answers, { ...answers, buyerType }) });
        }}
        testIdPrefix="call-buyer-type"
      />

      {company && <CompanyFields answers={answers} onChange={onChange} funnelCopy={funnelCopy} />}

      <CurrencyField
        label={company ? copy("label-buyer-capital-company") : copy("label-buyer-cash")}
        helperText={company ? copy("helper-buyer-capital-company") : copy("helper-buyer-cash-note-holder")}
        value={moneyText(answers.cashAvailable)}
        onChange={(raw) => onChange({ cashAvailable: parseMoney(raw), cashWontShare: false })}
        testId="call-cash"
      />
      <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
        <input
          type="checkbox"
          className="mt-[3px] accent-gold"
          checked={Boolean(answers.cashWontShare) && answers.cashAvailable == null}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { cashWontShare: true, cashAvailable: null }
                : { cashWontShare: false },
            )
          }
          data-testid="call-cash-wont-share"
        />
        <span>{copy("label-buyer-cash-wont-share")}</span>
      </label>

      {company && (
        <ChoiceField
          label={copy("question-buyer-equity-source")}
          options={[
            { value: "CompanyFundsOnHand", label: copy("option-buyer-equity-source-on-hand") },
            { value: "CommittedByInvestors", label: copy("option-buyer-equity-source-committed") },
            { value: "StillBeingRaised", label: copy("option-buyer-equity-source-raising") },
          ]}
          value={answers.equitySource ?? null}
          onChange={(v) => onChange({ equitySource: v as PrequalEquitySource })}
          testIdPrefix="call-equity-source"
        />
      )}

      {/* The funnel asks a company buyer for its equity contribution and no
          other assets; so does the call. */}
      {!company && <OtherAssets answers={answers} onChange={onChange} funnelCopy={funnelCopy} />}

      <CurrencyField
        label={copy("label-buyer-reserves")}
        helperText={copy("helper-buyer-reserves")}
        value={moneyText(answers.reservesAmount)}
        onChange={(raw) => onChange({ reservesAmount: parseMoney(raw) })}
        testId="call-reserves-amount"
      />
      <ChoiceField
        label={copy("question-buyer-reserves-months")}
        options={optionsFromCopy(RESERVES_BANDS, "option-buyer-reserves", callQuestions(funnelCopy))}
        value={answers.reservesBand ?? null}
        onChange={(v) => onChange({ reservesBand: v })}
        testIdPrefix="call-reserves"
      />

      {/* The band and the deal's price are two arms of one question: a priced
          deal answers it, so the band is asked only without one. */}
      {!pricesADeal && (
        <ChoiceField
          label={copy("question-buyer-price-band")}
          options={PRICE_BANDS}
          value={answers.priceBand ?? null}
          onChange={(v) => onChange({ priceBand: v })}
          testIdPrefix="call-price-band"
        />
      )}
    </CallBlock>
  );
}

function CompanyFields({ answers, onChange, funnelCopy }: CallFieldsProps) {
  const copy = (block: CallFunnelCopyBlock) => callCopy(funnelCopy, block);
  return (
    <div className="grid gap-space-3" data-testid="call-company-details">
      <FormField label={copy("label-buyer-company-name")}>
        <Input
          maxLength={COMPANY_FIELD_LIMITS.companyName}
          value={answers.acquiringCompanyName ?? ""}
          onChange={(e) => onChange({ acquiringCompanyName: e.target.value })}
          data-testid="call-company-name"
        />
      </FormField>
      <FormField label={copy("label-buyer-company-title")}>
        <Input
          maxLength={COMPANY_FIELD_LIMITS.title}
          value={answers.acquiringCompanyTitle ?? ""}
          onChange={(e) => onChange({ acquiringCompanyTitle: e.target.value })}
          data-testid="call-company-title"
        />
      </FormField>
      <FormField label={copy("label-buyer-company-website")}>
        <Input
          type="url"
          inputMode="url"
          maxLength={COMPANY_FIELD_LIMITS.website}
          value={answers.acquiringCompanyWebsite ?? ""}
          onChange={(e) => onChange({ acquiringCompanyWebsite: e.target.value })}
          data-testid="call-company-website"
        />
      </FormField>
      <FormField
        label={copy("label-buyer-company-about")}
        helperText={copy("helper-buyer-company-about")}
      >
        <Textarea
          rows={3}
          maxLength={COMPANY_FIELD_LIMITS.about}
          value={answers.acquiringCompanyAbout ?? ""}
          onChange={(e) => onChange({ acquiringCompanyAbout: e.target.value })}
          data-testid="call-company-about"
        />
      </FormField>
    </div>
  );
}

/**
 * The funnel's non-cash categories with their amounts. A category is TICKED
 * locally and only an amount above zero is stored — the wire's rule: absent
 * means "not named", never a selected-but-zero category that would print on a
 * letter as though it were one.
 */
function OtherAssets({ answers, onChange, funnelCopy }: CallFieldsProps) {
  const copy = (block: CallFunnelCopyBlock) => callCopy(funnelCopy, block);
  const stored = answers.cashEquivalents ?? null;
  const [ticked, setTicked] = useState<Set<OtherAssetKey>>(
    () => new Set(OTHER_ASSET_CATEGORIES.filter((key) => (stored?.[key] ?? 0) > 0)),
  );

  const write = (key: OtherAssetKey, amount: number | null) => {
    const next = { ...(stored ?? {}) };
    if (amount != null && amount > 0) next[key] = amount;
    else delete next[key];
    const named = OTHER_ASSET_CATEGORIES.some((k) => (next[k] ?? 0) > 0);
    onChange({ cashEquivalents: named || next.note ? next : null });
  };

  return (
    <fieldset className="border border-border-light rounded-md p-space-3 m-0">
      <legend className="px-space-1 text-small text-onyx">{copy("heading-buyer-other-assets")}</legend>
      <div className="grid gap-space-2">
        {OTHER_ASSET_CATEGORIES.map((key) => {
          // The funnel's block for this category — `option-buyer-other-asset-<key>`.
          const label = callQuestions(funnelCopy)(questionBlock(`option-buyer-other-asset-${blockSlug(key)}`));
          return (
            <div key={key}>
              <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-[3px] accent-gold"
                  checked={ticked.has(key)}
                  onChange={(e) => {
                    const next = new Set(ticked);
                    if (e.target.checked) next.add(key);
                    else {
                      next.delete(key);
                      // Unticking clears the hidden figure, as the funnel does.
                      write(key, null);
                    }
                    setTicked(next);
                  }}
                  data-testid={`call-asset-${key}`}
                />
                <span>{label}</span>
              </label>
              {ticked.has(key) && (
                <div className="mt-space-2 pl-space-3 border-l-2 border-gold">
                  <CurrencyField
                    label={`${label} — amount`}
                    value={moneyText(stored?.[key])}
                    onChange={(raw) => write(key, parseMoney(raw))}
                    testId={`call-asset-${key}-amount`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {ticked.size > 0 && (
        <div className="mt-space-3">
          <FormField
            label={copy("label-buyer-other-assets-note")}
            helperText={copy("helper-buyer-other-assets-note")}
          >
            <Textarea
              rows={2}
              value={stored?.note ?? ""}
              onChange={(e) =>
                onChange({ cashEquivalents: { ...(stored ?? {}), note: e.target.value || null } })
              }
              data-testid="call-assets-note"
            />
          </FormField>
        </div>
      )}
    </fieldset>
  );
}

// ── Experience ───────────────────────────────────────────────────────────

/** EXP on the call: the funnel's three ladders, plus the one-tap follow-up each
 *  of two of them needs for the interview to know which way the answer reads. */
export function CallExperienceFields({ answers, onChange, funnelCopy }: CallFieldsProps) {
  const copy = (block: CallFunnelCopyBlock) => callCopy(funnelCopy, block);
  const q = callQuestions(funnelCopy);
  const hasOwned = (answers.ownershipExperience ?? "").startsWith("owned-");
  return (
    <CallBlock testId="call-fields-EXP">
      <ChoiceField
        label={copy("question-buyer-ownership")}
        options={optionsFromCopy(OWNERSHIP_LADDER, "option-buyer-ownership", q)}
        value={answers.ownershipExperience ?? null}
        onChange={(v) =>
          onChange({
            ownershipExperience: v,
            // A follow-up whose question is gone keeps no answer.
            ownsNow: v.startsWith("owned-") ? answers.ownsNow ?? null : null,
          })
        }
        testIdPrefix="call-ownership"
      />
      {hasOwned && (
        <ChoiceField
          label={copy("question-buyer-owns-now")}
          options={YES_NO_OPTIONS}
          value={yesNo(answers.ownsNow)}
          onChange={(v) => onChange({ ownsNow: v === "yes" })}
          testIdPrefix="call-owns-now"
        />
      )}
      <ChoiceField
        label={copy("question-buyer-management")}
        options={optionsFromCopy(MANAGEMENT_LADDER, "option-buyer-management", q)}
        value={answers.managementExperience ?? null}
        onChange={(v) => onChange({ managementExperience: v })}
        testIdPrefix="call-management"
      />
      <ChoiceField
        label={copy("question-buyer-industry-fit")}
        options={optionsFromCopy(INDUSTRY_LADDER, "option-buyer-industry-fit", q)}
        value={answers.industryFit ?? null}
        onChange={(v) =>
          onChange({
            industryFit: v,
            hasResearchedIndustry: v === "new" ? answers.hasResearchedIndustry ?? null : null,
          })
        }
        testIdPrefix="call-industry-fit"
      />
      {answers.industryFit === "new" && (
        <ChoiceField
          label={copy("question-buyer-researched-industry")}
          options={YES_NO_OPTIONS}
          value={yesNo(answers.hasResearchedIndustry)}
          onChange={(v) => onChange({ hasResearchedIndustry: v === "yes" })}
          testIdPrefix="call-researched"
        />
      )}
    </CallBlock>
  );
}

// ── Motivation & fit ─────────────────────────────────────────────────────

/**
 * MF on the call: involvement, then the funnel's full-time-management follow-up
 * in the funnel's structure (FT) — a company buyer not operating it: who will
 * manage it (above AAMA, as the funnel orders them) and the AAMA question; an
 * individual overseer: the requirement and the five-choice question; a
 * hands-off individual: the hands-off requirement and the AAMA question only.
 */
export function CallMotivationFields({ answers, onChange, funnelCopy }: CallFieldsProps) {
  const copy = (block: CallFunnelCopyBlock) => callCopy(funnelCopy, block);
  const followUp = callFollowUp(answers);
  const arrangement =
    followUp === "company" || followUp === "overseer" ? (answers.operatorArrangement ?? null) : null;

  return (
    <CallBlock testId="call-fields-MF">
      <ChoiceField
        label={copy("question-buyer-involvement")}
        options={optionsFromCopy(INVOLVEMENT_OPTIONS, "option-buyer-involvement", callQuestions(funnelCopy))}
        value={answers.involvement ?? null}
        onChange={(v) =>
          onChange({ involvement: v, ...staleFollowUpClears(answers, { ...answers, involvement: v }) })
        }
        testIdPrefix="call-involvement"
      />
      {followUp === "overseer" && (
        <>
          <p className="text-small text-gray-mid m-0" data-testid="call-management-requirement">
            {copy("requirement-buyer-managed-full-time")}
          </p>
          <ChoiceField
            label={copy("question-buyer-full-time-manager")}
            options={[
              { value: "HiredGeneralManager", label: copy("option-buyer-manager-gm-individual") },
              { value: "ExistingManagement", label: copy("option-buyer-manager-existing-individual") },
              { value: "DesignatedPerson", label: copy("option-buyer-manager-designated-person") },
              { value: "aama", label: copy("option-buyer-manager-aama") },
              { value: "undecided", label: copy("option-buyer-manager-undecided") },
            ]}
            value={managerChoiceOf(answers)}
            onChange={(v) => onChange(managerChoicePatch(v))}
            testIdPrefix="call-manager"
          />
        </>
      )}
      {followUp === "company" && (
        <ChoiceField
          label={copy("question-buyer-full-time-manager")}
          helperText={copy("helper-buyer-full-time-manager")}
          options={[
            { value: "CompanyPrincipal", label: copy("option-buyer-operator-principal") },
            { value: "HiredGeneralManager", label: copy("option-buyer-operator-general-manager") },
            { value: "ExistingManagement", label: copy("option-buyer-operator-existing-management") },
          ]}
          value={arrangement}
          onChange={(v) => onChange({ operatorArrangement: v as PrequalOperatorArrangement })}
          testIdPrefix="call-operator"
        />
      )}
      {arrangement && (
        <FormField
          label={
            arrangement === "HiredGeneralManager"
              ? copy("label-buyer-operator-name-optional")
              : copy("label-buyer-operator-name")
          }
        >
          <Input
            maxLength={COMPANY_FIELD_LIMITS.operatorName}
            value={answers.operatorName ?? ""}
            onChange={(e) => onChange({ operatorName: e.target.value })}
            data-testid="call-operator-name"
          />
        </FormField>
      )}
      {followUp === "hands-off" && (
        <p className="text-small text-gray-mid m-0" data-testid="call-management-requirement">
          {copy("requirement-buyer-managed-full-time-hands-off")}
        </p>
      )}
      {(followUp === "company" || followUp === "hands-off") && (
        <ChoiceField
          label={copy("question-buyer-aama")}
          options={[
            { value: "yes", label: copy("option-buyer-aama-yes") },
            { value: "no", label: copy("option-buyer-aama-neither") },
          ]}
          value={yesNo(answers.aamaPath)}
          onChange={(v) => onChange({ aamaPath: v === "yes" })}
          testIdPrefix="call-aama"
        />
      )}
    </CallBlock>
  );
}

// ── Prequalification (optional, D8) ──────────────────────────────────────

export const REVIEW_STATUS_LABEL: Record<BuyerPrequalCallSubmissionDto["reviewStatus"], string> = {
  awaitingReview: "Awaiting review",
  sent: "Sent",
  declineSent: "Decline documents sent",
  withheld: "Not sent",
};

/**
 * The prequalification itself: which documents (the funnel's three arms), the
 * deal on the two arms that price one — picked from the buyer's listings or
 * typed in — the internal arm's questions, the seven fit-check questions, who
 * the documents are made out to, the consent read aloud, and Prequalify.
 *
 * <p>Optional (D8): the interview completes without it, and answers given here
 * persist, so it can be finished later from the same screen.</p>
 */
export function CallPrequalificationSection({
  buyerLeadId,
  buyerName,
  answers,
  onChange,
  state,
  onPrequalify,
  prequalifying,
  onReview,
}: CallFieldsProps & {
  buyerLeadId: string;
  buyerName?: string;
  state: BuyerPrequalCallStateDto | undefined;
  onPrequalify: () => void;
  prequalifying: boolean;
  onReview: (submissionId: string) => void;
}) {
  const qc = useQueryClient();
  const path = answers.documentPath ?? null;
  const onDealPath = path === DOCUMENT_PATH_WITH_DEAL_SHEET;
  const onInternalPath = path === DOCUMENT_PATH_INTERNAL;
  const statesADeal = onDealPath || onInternalPath;
  // The caution follows a figure the LISTING supplied; once the specialist
  // types over it, it is their figure and the caution would be false.
  const [cashFlowEdited, setCashFlowEdited] = useState(false);
  const showCaution =
    Boolean(answers.listingId) && answers.targetAnnualCashFlow != null && !cashFlowEdited;

  const picked: PickedListing | null = answers.listingId
    ? {
        id: answers.listingId,
        businessName: answers.targetBusinessName ?? null,
        askingPrice: answers.targetPurchasePrice ?? null,
      }
    : null;

  const pickListing = async (listing: PickedListing) => {
    // The name and the price arrive with the row; the cash flow may need the
    // listing read, so the pick is written in one go once it is known.
    const cashFlow = await resolvePickedListingCashFlow(qc, listing);
    setCashFlowEdited(false);
    onChange({
      listingId: listing.id,
      targetBusinessName: listing.businessName ?? listing.displayName ?? answers.targetBusinessName ?? null,
      targetPurchasePrice: listing.askingPrice ?? answers.targetPurchasePrice ?? null,
      targetAnnualCashFlow: cashFlow ?? answers.targetAnnualCashFlow ?? null,
    });
  };

  const latest = state?.latestCallPrequal ?? null;
  const copy = (block: CallFunnelCopyBlock) => callCopy(state?.funnelCopy, block);
  const q = callQuestions(state?.funnelCopy);

  return (
    <div className="grid gap-space-4" data-testid="call-prequal-section">
      <fieldset className="border-0 p-0 m-0 grid gap-space-2">
        <legend className="text-small text-onyx mb-space-2">{copy("label-buyer-path-call")}</legend>
        {DOCUMENT_PATH_CARDS.map((card) => {
          // The funnel's card blocks — `option-buyer-path-<path>[-body|-produces]`.
          const block = `option-buyer-path-${blockSlug(card.value)}`;
          return (
          <button
            key={card.value}
            type="button"
            aria-pressed={path === card.value}
            onClick={() =>
              onChange({ documentPath: card.value as BuyerCallAnswers["documentPath"] })
            }
            data-testid={`call-path-${card.value}`}
            className={cn(
              "w-full text-left rounded-md border px-space-3 py-space-2",
              "transition-colors duration-fast ease-standard",
              path === card.value ? "border-gold bg-white-warm" : "border-border-light hover:bg-white-warm",
            )}
          >
            <span className="block text-small text-onyx">{q(questionBlock(block))}</span>
            <span className="block text-caption text-gray-mid">{q(questionBlock(`${block}-body`))}</span>
            <span className="block text-caption text-gray-mid">{q(questionBlock(`${block}-produces`))}</span>
          </button>
          );
        })}
      </fieldset>

      {onInternalPath && (
        <>
          <ChoiceField
            label={copy("question-buyer-internal-relationship")}
            options={optionsFromCopy(INTERNAL_RELATIONSHIP_OPTIONS, "option-buyer-internal-relationship", q)}
            value={answers.internalRelationship ?? null}
            onChange={(v) =>
              onChange({ internalRelationship: v as PrequalInternalRelationship })
            }
            testIdPrefix="call-internal-relationship"
          />
          {answers.internalRelationship === INTERNAL_RELATIONSHIP_WITH_TENURE && (
            <FormField
              label={copy("label-buyer-internal-years")}
              helperText={copy("helper-buyer-internal-years")}
            >
              <Input
                type="number"
                min={0}
                inputMode="numeric"
                value={answers.internalYearsWithBusiness ?? ""}
                onChange={(e) => onChange({ internalYearsWithBusiness: wholeNumber(e.target.value) })}
                data-testid="call-internal-years"
              />
            </FormField>
          )}
          <FormField
            label={copy("label-buyer-internal-co-buyers")}
            helperText={copy("helper-buyer-internal-co-buyers")}
          >
            <Input
              type="number"
              min={0}
              inputMode="numeric"
              value={answers.internalCoBuyerCount ?? ""}
              onChange={(e) => onChange({ internalCoBuyerCount: wholeNumber(e.target.value) })}
              data-testid="call-internal-co-buyers"
            />
          </FormField>
        </>
      )}

      {statesADeal && (
        <ListingPicker
          selected={picked}
          onSelect={(l) => void pickListing(l)}
          // Clearing the listing keeps what was prefilled, editable: the typed
          // fallback is these same fields with no listing behind them.
          onClear={() => onChange({ listingId: null })}
          enabled
          buyerLeadId={buyerLeadId}
          buyerName={buyerName}
          allowPresented
          tiers={["shortlist", "search"]}
        />
      )}

      <FormField
        label={copy(
          onInternalPath
            ? "label-buyer-target-business-internal"
            : onDealPath
              ? "label-buyer-target-business-deal"
              : "label-buyer-target-business-letter-only",
        )}
        helperText={copy(
          onInternalPath
            ? "helper-buyer-target-business-internal"
            : onDealPath
              ? "helper-buyer-target-business-deal"
              : "helper-buyer-target-business-letter-only",
        )}
      >
        <Input
          value={answers.targetBusinessName ?? ""}
          onChange={(e) => onChange({ targetBusinessName: e.target.value })}
          data-testid="call-business-name"
        />
      </FormField>

      {statesADeal && (
        <>
          <CurrencyField
            label={copy("label-buyer-asking-price")}
            helperText={copy(
              onInternalPath ? "helper-buyer-asking-price-internal" : "helper-buyer-asking-price-deal",
            )}
            value={moneyText(answers.targetPurchasePrice)}
            onChange={(raw) => onChange({ targetPurchasePrice: parseMoney(raw) })}
            testId="call-price"
          />
          <div>
            <CurrencyField
              label={copy(onInternalPath ? "label-buyer-cash-flow-internal" : "label-buyer-cash-flow-deal")}
              helperText={copy(
                onInternalPath ? "helper-buyer-free-cash-flow-internal" : "helper-buyer-free-cash-flow-deal",
              )}
              value={moneyText(answers.targetAnnualCashFlow)}
              onChange={(raw) => {
                setCashFlowEdited(true);
                onChange({ targetAnnualCashFlow: parseMoney(raw) });
              }}
              testId="call-cash-flow"
            />
            {showCaution && (
              <p
                className="text-caption text-status-warning-ink m-0 mt-space-1"
                data-testid="call-cash-flow-caution"
              >
                {LISTING_CASH_FLOW_CAUTION}
              </p>
            )}
          </div>
          <FormField label={copy("label-buyer-close-date")} helperText={copy("helper-buyer-close-date")}>
            {/* A wall-calendar day, sent as the input's own YYYY-MM-DD. */}
            <Input
              type="date"
              value={answers.targetPurchaseDate ?? ""}
              onChange={(e) => onChange({ targetPurchaseDate: e.target.value || null })}
              data-testid="call-close-date"
            />
          </FormField>
        </>
      )}

      <div data-testid="call-knockouts">
        <KnockoutChecklist
          questions={BUYER_KNOCKOUTS}
          copy={q}
          answers={answers.knockouts ?? {}}
          onAnswer={(key, value) =>
            // A function patch: one click writes three keys in a row (primary,
            // follow-up, resolved), so each must merge into the LATEST knockouts.
            onChange((latest) => ({ knockouts: { ...(latest.knockouts ?? {}), [key]: value } }))
          }
        />
      </div>

      <FormField label={copy("step-title-buyer-goals")} helperText={copy("helper-buyer-goals")}>
        <Textarea
          rows={3}
          value={answers.goals ?? ""}
          onChange={(e) => onChange({ goals: e.target.value })}
          data-testid="call-goals"
        />
      </FormField>

      <div className="grid gap-space-3" data-testid="call-contact">
        <FormField label={copy("label-buyer-first-name")}>
          <Input
            value={answers.firstName ?? ""}
            onChange={(e) => onChange({ firstName: e.target.value })}
            data-testid="call-first-name"
          />
        </FormField>
        <FormField label={copy("label-buyer-last-name")}>
          <Input
            value={answers.lastName ?? ""}
            onChange={(e) => onChange({ lastName: e.target.value })}
            data-testid="call-last-name"
          />
        </FormField>
        <FormField label={copy("label-buyer-email")}>
          <Input
            type="email"
            value={answers.email ?? ""}
            onChange={(e) => onChange({ email: e.target.value })}
            data-testid="call-email"
          />
        </FormField>
        <PhoneField
          label={copy("label-buyer-phone")}
          value={answers.phone ?? ""}
          onChange={(digits) => onChange({ phone: digits })}
          testId="call-phone"
        />
      </div>

      <div className="grid gap-space-2 rounded-md border border-border-light p-space-3" data-testid="call-consent">
        <p className="text-caption uppercase tracking-wide text-gray-mid m-0">
          Read this to the buyer
        </p>
        <blockquote
          className="m-0 border-l-2 border-gold pl-space-3 text-small text-onyx"
          data-testid="call-consent-script"
        >
          {state?.consentScript ?? ""}
        </blockquote>
        <label className="flex items-start gap-space-2 text-small text-onyx cursor-pointer">
          <input
            type="checkbox"
            className="mt-[3px] accent-gold"
            checked={Boolean(answers.consentAccepted)}
            onChange={(e) => onChange({ consentAccepted: e.target.checked })}
            data-testid="call-consent-accepted"
          />
          <span data-testid="call-certification">{state?.certificationText ?? ""}</span>
        </label>
      </div>

      <div className="grid gap-space-2">
        <div>
          <Button
            onClick={onPrequalify}
            loading={prequalifying}
            disabled={!state?.canPrequalify || prequalifying}
            data-testid="call-prequalify"
          >
            Prequalify
          </Button>
        </div>
        {(state?.blockedReasons.length ?? 0) > 0 && (
          <ul className="m-0 pl-space-4 text-small text-gray-mid" role="status" data-testid="call-blocked-reasons">
            {state?.blockedReasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        )}
        <p className="text-caption text-gray-mid m-0">
          Prequalifying generates the documents and sends nothing. You review them, and the
          email, before anything goes to the buyer.
        </p>
      </div>

      {latest && (
        <div
          className="flex flex-wrap items-center gap-space-2 text-small"
          data-testid="call-latest-prequal"
        >
          <span className="text-onyx">
            Prequalified{latest.referenceNumber ? ` · ${latest.referenceNumber}` : ""}
          </span>
          <span className="text-gray-mid">{REVIEW_STATUS_LABEL[latest.reviewStatus]}</span>
          {latest.reviewStatus === "awaitingReview" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onReview(latest.submissionId)}
              data-testid="call-review-and-send"
            >
              Review &amp; send
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
